import * as React from "react";
import PropTypes from "prop-types";
import {
  Body1,
  makeStyles,
  tokens,
  Dropdown,
  Option,
  Checkbox,
  Button,
  Divider,
  Input,
  Textarea,
  Field,
  Spinner
} from "@fluentui/react-components";
import { AttachRegular, Delete20Regular } from "@fluentui/react-icons";
import { Modal } from "../modal/Modal";
import { FormattedMessage, useIntl } from "react-intl";
import dashboardChannelSingleton from "../../utils/dashboard-channel-singleton";
import notificationsCenter from "../../singleton/NotificationsCenter";

const useStyles = makeStyles({
  dialog: {
    maxHeight: "90vh",
    overflowY: "auto"
  },
  description: {
    marginBottom: tokens.spacingVerticalXXL,
    display: "block"
  },
  field: {
    marginBottom: tokens.spacingVerticalL
  },
  fieldLabel: {
    color: "var(--Gray-3, #828282)"
  },
  dropdownContainer: {
    marginBottom: tokens.spacingVerticalL
  },
  dropdown: {
    width: "100%"
  },
  dropdownLabel: {
    display: "block",
    marginBottom: tokens.spacingVerticalS,
    color: "var(--Gray-3, #828282)"
  },
  checkboxContainer: {
    marginBottom: tokens.spacingVerticalL
  },
  checkboxGroup: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    "@media (min-width: 768px)": {
      flexDirection: "row",
      gap: tokens.spacingHorizontalM
    }
  },
  checkboxLabel: {
    display: "block",
    marginBottom: tokens.spacingVerticalS,
    color: "var(--Gray-3, #828282)"
  },
  fileUpload: {
    marginBottom: tokens.spacingVerticalL
  },
  fileUploadContainer: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalS
  },
  fileUploadLabel: {
    color: "var(--Gray-3, #828282)"
  },
  fileInput: {
    display: "none"
  },
  uploadButton: {
    marginBottom: tokens.spacingVerticalS
  },
  previewImage: {
    maxWidth: "100%",
    maxHeight: "200px",
    marginTop: tokens.spacingVerticalS,
    borderRadius: "4px"
  },
  uploadedCard: {
    display: "flex",
    alignItems: "center",
    background: "var(--primaryColorBackground)",
    borderRadius: tokens.borderRadiusXLarge,
    paddingRight: tokens.spacingHorizontalM,
    minHeight: "64px",
    marginTop: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalS,
    boxShadow: "0 2px 8px 0 rgba(0,0,0,0.10)",
    width: "100%",
    gap: "1rem"
  },
  uploadedThumb: {
    width: "64px",
    height: "64px",
    objectFit: "cover",
    borderTopLeftRadius: tokens.borderRadiusXLarge,
    borderBottomLeftRadius: tokens.borderRadiusXLarge
  },
  uploadedInfo: {
    flex: 1,
    color: "#fff",
    fontWeight: 600,
    maxWidth: "80%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  uploadedDelete: {
    color: "var(--Red, #EB5757)",
    cursor: "pointer",
    marginLeft: "auto"
  },
  dividerSpacing: {
    marginTop: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalM
  },
  errorText: {
    color: "var(--red)",
    fontSize: "12px",
    marginTop: "4px"
  },
  requiredFieldLabel: {
    color: "var(--Gray-3, #828282)",
    "&::after": {
      content: '" *"',
      color: "var(--Red, #EB5757)"
    }
  }
});

export const ReportBugDialog = ({ onConfirm, onClose, open = false }) => {
  const intl = useIntl();

  // Get the dashboard channel instance
  const dashboardChannel = dashboardChannelSingleton.getExistingInstance();

  // State for tracking the form field values
  const [subject, setSubject] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [severity, setSeverity] = React.useState("");
  const [selectedSeverityOptions, setSelectedSeverityOptions] = React.useState([]);
  const [devices, setDevices] = React.useState({
    desktop: false,
    mobile: false,
    vr: false
  });
  const [screenshot, setScreenshot] = React.useState(null);
  const [screenshotPreview, setScreenshotPreview] = React.useState("");

  // State for validation errors
  const [subjectError, setSubjectError] = React.useState("");
  const [descriptionError, setDescriptionError] = React.useState("");
  const [severityError, setSeverityError] = React.useState("");
  const [disable, setDisable] = React.useState(true);

  // State for loading and submission
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // File input reference
  const fileInputRef = React.useRef(null);

  // Handle subject input change
  const handleSubjectChange = event => {
    if (isSubmitting) return; // Disable input when submitting

    const value = event.target.value;
    setSubject(value);

    if (value.length < 5) {
      setSubjectError("Subject must be at least 5 characters long");
    } else if (value.length > 100) {
      setSubjectError("Subject must be less than 100 characters");
    } else {
      setSubjectError("");
    }
  };

  // Handle description input change
  const handleDescriptionChange = event => {
    if (isSubmitting) return; // Disable input when submitting

    const value = event.target.value;
    setDescription(value);

    if (value.length < 10) {
      setDescriptionError("Description must be at least 10 characters long");
    } else if (value.length > 500) {
      setDescriptionError("Description must be less than 500 characters");
    } else {
      setDescriptionError("");
    }
  };

  // Handle severity selection
  const handleSeverityChange = (_, data) => {
    if (isSubmitting) return; // Disable input when submitting

    setSeverity(data.optionText || "");
    setSelectedSeverityOptions(data.selectedOptions || []);
    setSeverityError("");
  };

  // Handle device checkbox changes
  const handleDeviceChange = (event, data) => {
    if (isSubmitting) return; // Disable input when submitting

    const name = event.target.name;
    setDevices({
      ...devices,
      [name]: data.checked
    });
  };

  // Handle file upload button click
  const handleUploadClick = () => {
    if (isSubmitting) return; // Disable input when submitting
    fileInputRef.current.click();
  };

  // Handle file selection
  const handleFileChange = event => {
    if (isSubmitting) return; // Disable input when submitting

    const file = event.target.files[0];
    const maxSizeInBytes = 5 * 1024 * 1024; // 5MB

    if (file && file.type.startsWith("image/")) {
      if (file.size > maxSizeInBytes) {
        alert("File size exceeds the 5MB limit. Please select a smaller file.");
        return;
      }

      setScreenshot(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Update disable logic based on form validation
  React.useEffect(() => {
    setDisable(
      isSubmitting ||
        subject.length < 5 ||
        subject.length > 100 ||
        description.length < 10 ||
        description.length > 500 ||
        !!subjectError ||
        !!descriptionError ||
        !!severityError
    );
  }, [subject, description, severity, subjectError, descriptionError, severityError, isSubmitting]);

  // Handle confirmation action (when the Send button is clicked)
  const handleConfirm = async () => {
    // Validate required fields
    if (subject.length < 5) {
      setSubjectError("Subject must be at least 5 characters long");
      return;
    }

    if (description.length < 10) {
      setDescriptionError("Description must be at least 10 characters long");
      return;
    }

    // Check if dashboard channel is available
    if (!dashboardChannel) {
      console.error("Dashboard channel not available");
      notificationsCenter.error("Failed to submit bug report", "Dashboard channel not available");
      return;
    }

    // Set submitting state
    setIsSubmitting(true);

    // Prepare the bug report data
    const bugData = {
      subject,
      description,
      severity,
      // transform to string with comma-separated values
      devices: Object.entries(devices)
        .filter(([_, checked]) => checked)
        .map(([device]) => device)
        .join(", "),
      screenshot
    };

    try {
      // Call the dashboard channel's reportBug method
      const result = await dashboardChannel.reportBug(bugData);

      if (result) {
        notificationsCenter.success("Bug Report Submitted", "Thank you for your feedback! We'll review it shortly.");

        // Close the dialog on success
        onClose();

        // Reset form only on success
        setSubject("");
        setDescription("");
        setSeverity("");
        setSelectedSeverityOptions([]);
        setDevices({
          desktop: false,
          mobile: false,
          vr: false
        });
        setScreenshot(null);
        setScreenshotPreview("");
      } else {
        console.error("Failed to submit bug report");
        notificationsCenter.error("Failed to Submit Bug Report", "Please try again later.");
        // Don't reset form on error - keep user's data
      }
    } catch (error) {
      console.error("Error submitting bug report:", error);
      notificationsCenter.error("Failed to Submit Bug Report", "An error occurred while submitting your report.");
      // Don't reset form on error - keep user's data
    } finally {
      // Reset submitting state
      setIsSubmitting(false);
    }

    // Call the original onConfirm callback if provided
    if (onConfirm) {
      onConfirm(bugData);
    }
  };

  const handleRemoveScreenshot = () => {
    if (isSubmitting) return; // Disable input when submitting
    setScreenshot(null);
    setScreenshotPreview("");
  };

  // Helper function to truncate filename while preserving extension
  const truncateFileName = (fileName, maxLength = 30) => {
    if (!fileName || fileName.length <= maxLength) {
      return fileName;
    }

    const lastDotIndex = fileName.lastIndexOf(".");
    if (lastDotIndex === -1) {
      // No extension, just truncate
      return fileName.substring(0, maxLength - 3) + "...";
    }

    const name = fileName.substring(0, lastDotIndex);
    const extension = fileName.substring(lastDotIndex);

    const maxNameLength = maxLength - extension.length - 3; // 3 for "..."
    if (maxNameLength <= 0) {
      return "..." + extension;
    }

    return name.substring(0, maxNameLength) + "..." + extension;
  };

  const handleOpenChange = (event, data) => {
    if (!data.open) {
      onClose();
    }
  };

  const styles = useStyles();

  const actions = (
    <>
      <Button appearance="outline" onClick={onClose} disabled={isSubmitting}>
        <FormattedMessage id="button.cancel" defaultMessage="Cancel" />
      </Button>
      <Button appearance="primary" onClick={handleConfirm} disabled={disable}>
        {isSubmitting ? (
          <>
            <Spinner size="tiny" />
            <FormattedMessage id="button.sending" defaultMessage="Sending..." />
          </>
        ) : (
          <FormattedMessage id="button.send" defaultMessage="Send" />
        )}
      </Button>
    </>
  );

  return (
    <Modal
      title="Help Us To Improve!"
      open={open}
      onOpenChange={handleOpenChange}
      onClose={onClose}
      actions={actions}
      className={styles.dialog}
    >
      <Body1 className={styles.description}>
        <FormattedMessage
          id="report-bug.description"
          defaultMessage="Thank you for taking the time to report bugs. We appreciate your collaboration."
        />
      </Body1>

      <Field
        label={
          <span className={styles.requiredFieldLabel}>
            <FormattedMessage id="report-bug.subject" defaultMessage="Bug/Issue" />
          </span>
        }
        validationMessage={subjectError}
        className={styles.field}
      >
        <Input
          value={subject}
          onChange={handleSubjectChange}
          placeholder={intl.formatMessage({ id: "report-bug.subject-placeholder", defaultMessage: "Subject..." })}
          appearance="filled-darker"
          disabled={isSubmitting}
        />
      </Field>

      <Field
        label={
          <span className={styles.requiredFieldLabel}>
            <FormattedMessage id="report-bug.description-label" defaultMessage="Description" />
          </span>
        }
        validationMessage={descriptionError}
        className={styles.field}
      >
        <Textarea
          value={description}
          onChange={handleDescriptionChange}
          placeholder={intl.formatMessage({
            id: "report-bug.description-placeholder",
            defaultMessage: "Add a brief description of the problem..."
          })}
          appearance="filled-darker"
          resize="vertical"
          rows={4}
          disabled={isSubmitting}
        />
      </Field>

      <div className={styles.dropdownContainer}>
        <label className={styles.dropdownLabel}>
          <FormattedMessage id="report-bug.severity" defaultMessage="Severity" />
        </label>
        <Dropdown
          placeholder={intl.formatMessage({
            id: "report-bug.severity-placeholder",
            defaultMessage: "Select the severity of the problem"
          })}
          value={severity}
          selectedOptions={selectedSeverityOptions}
          onOptionSelect={handleSeverityChange}
          appearance="filled-darker"
          className={styles.dropdown}
          disabled={isSubmitting}
        >
          <Option text="Critical" value="critical">
            <FormattedMessage id="report-bug.severity.critical" defaultMessage="Critical" />
          </Option>
          <Option text="High" value="high">
            <FormattedMessage id="report-bug.severity.high" defaultMessage="High" />
          </Option>
          <Option text="Medium" value="medium">
            <FormattedMessage id="report-bug.severity.medium" defaultMessage="Medium" />
          </Option>
          <Option text="Low" value="low">
            <FormattedMessage id="report-bug.severity.low" defaultMessage="Low" />
          </Option>
        </Dropdown>
        {severityError && <span className={styles.errorText}>{severityError}</span>}
      </div>

      <div className={styles.checkboxContainer}>
        <label className={styles.checkboxLabel}>
          <FormattedMessage id="report-bug.devices" defaultMessage="Device affected" />
        </label>
        <div className={styles.checkboxGroup}>
          <Checkbox
            label={<FormattedMessage id="report-bug.devices.desktop" defaultMessage="Desktop" />}
            name="desktop"
            checked={devices.desktop}
            onChange={handleDeviceChange}
            disabled={isSubmitting}
          />
          <Checkbox
            label={<FormattedMessage id="report-bug.devices.mobile" defaultMessage="Mobile" />}
            name="mobile"
            checked={devices.mobile}
            onChange={handleDeviceChange}
            disabled={isSubmitting}
          />
          <Checkbox
            label={<FormattedMessage id="report-bug.devices.vr" defaultMessage="VR" />}
            name="vr"
            checked={devices.vr}
            onChange={handleDeviceChange}
            disabled={isSubmitting}
          />
        </div>
      </div>

      <div className={styles.fileUpload}>
        <div className={styles.fileUploadContainer}>
          <label className={styles.fileUploadLabel}>
            <FormattedMessage id="report-bug.screenshot" defaultMessage="Attach screenshot" />
          </label>
          <Button
            icon={<AttachRegular />}
            onClick={handleUploadClick}
            appearance="primary"
            disabled={isSubmitting}
            aria-label={intl.formatMessage({ id: "report-bug.screenshot-aria", defaultMessage: "Attach screenshot" })}
          />
        </div>
        <input
          type="file"
          ref={fileInputRef}
          className={styles.fileInput}
          accept="image/*"
          onChange={handleFileChange}
          disabled={isSubmitting}
        />
        {screenshot && screenshotPreview && (
          <>
            <Divider className={styles.dividerSpacing} />
            <div className={styles.uploadedCard}>
              <img
                src={screenshotPreview}
                alt={truncateFileName(screenshot?.name) || "Screenshot preview"}
                className={styles.uploadedThumb}
              />
              <div className={styles.uploadedInfo}>{truncateFileName(screenshot?.name) || "Image"}</div>
              <Delete20Regular
                className={styles.uploadedDelete}
                aria-label={intl.formatMessage({
                  id: "report-bug.remove-screenshot",
                  defaultMessage: "Remove screenshot"
                })}
                onClick={handleRemoveScreenshot}
                tabIndex={0}
                role="button"
              />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

ReportBugDialog.propTypes = {
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool
};
